import type { NpcEncounter, NpcUnit } from "./types";
import { EFF, WAVE_GAP_SECONDS } from "./combatConstants";
import { parseCompositionUnits, parseStageSegments } from "./encounterComposition";

export type DifficultyBand = "Easy" | "Challenge" | "Very Hard" | "Almost Impossible";

export interface PlayerCombatStats {
  ehp: number;
  dps: number;
}

export interface MissionDifficultyStage {
  enemyEhp: number;
  enemyDps: number;
  ttkSeconds: number;
  damageTaken: number;
}

export interface MissionDifficultyResult {
  ttkSeconds: number;
  damageTaken: number;
  damageTakenPercent: number;
  band: DifficultyBand;
  stages: MissionDifficultyStage[];
}

// Locked thresholds, design doc Section 16.32 (16.29's citation of "16.20's
// methodology" is a mislabeling -- 16.20 is actually Per-System Danger Tier;
// 16.32 is the section that actually locks these band cutoffs).
export function bandDifficulty(damageTakenPercent: number): DifficultyBand {
  if (damageTakenPercent < 25) return "Easy";
  if (damageTakenPercent < 60) return "Challenge";
  if (damageTakenPercent < 100) return "Very Hard";
  return "Almost Impossible";
}

function stageDpsValues(dps: string): number[] {
  return dps.split("->").map((value) => Number(value.trim()));
}

// Per 16.12: Wave encounters resolve sequentially -- one stage's units are
// fully cleared before the next stage's units exist. The 15s recovery gap
// between stages (16.12/16.30) adds wall-clock time but no damage; shield/
// repair regen during the gap isn't modeled here, since that needs a player
// regen-rate input this function doesn't take.
export function computeMissionDifficulty(
  player: PlayerCombatStats,
  encounter: NpcEncounter,
  units: NpcUnit[]
): MissionDifficultyResult {
  const compositions = parseStageSegments(encounter.composition);
  const dpsValues = stageDpsValues(encounter.dps);
  if (compositions.length !== dpsValues.length) {
    throw new Error(
      `Composition/DPS stage count mismatch for ${encounter.id}: ` +
        `${compositions.length} composition segment(s) vs ${dpsValues.length} DPS value(s)`
    );
  }

  const playerEffectiveDps = player.dps * EFF;

  const stages: MissionDifficultyStage[] = compositions.map((composition, i) => {
    const unitCounts = parseCompositionUnits(composition, encounter.faction, encounter.hullTier, units);
    const enemyEhp = unitCounts.reduce(
      (sum, { unit, count }) => sum + count * (unit.hullHp + unit.shieldHp),
      0
    );
    const enemyDps = dpsValues[i];
    const enemyEffectiveDps = enemyDps * EFF;
    const ttkSeconds = playerEffectiveDps > 0 ? enemyEhp / playerEffectiveDps : Infinity;
    const damageTaken = enemyEffectiveDps * ttkSeconds;
    return { enemyEhp, enemyDps, ttkSeconds, damageTaken };
  });

  const combatTtkSeconds = stages.reduce((sum, s) => sum + s.ttkSeconds, 0);
  const gapSeconds = stages.length > 1 ? (stages.length - 1) * WAVE_GAP_SECONDS : 0;
  const damageTaken = stages.reduce((sum, s) => sum + s.damageTaken, 0);
  const damageTakenPercent = (damageTaken / player.ehp) * 100;

  return {
    ttkSeconds: combatTtkSeconds + gapSeconds,
    damageTaken,
    damageTakenPercent,
    band: bandDifficulty(damageTakenPercent),
    stages
  };
}
